import { FinancialStatistics } from '@/types/FinancialStatistics';
import { LabelValue, LabelValueYearT12 } from '@/types/label-value';
import { Service } from './api';
import { MultiSeries } from '@/types/MultiSeries';
import { colorsBySourceAgencyKey } from '@/utilities/Color';
import { agencyLegendOrder } from '@/utilities/Charts';

export interface PremiumByBillmodeData {
  billMode: string
  policyPremium: number
  value?: string
}

export interface PremiumByWholesaleVsRetailData {
  wholesale: number
  policyPremium: number
  value?: string
}

class ManagementAnalyticsService extends Service {
  async statistics(
    startDate: string | null,
    endDate: string | null,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<FinancialStatistics> {
    return await this.get<FinancialStatistics>('/v1/statistics/financials', {
      startDate,
      endDate,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse);
  }

  async premiumByMonth(
    startDate: string | null,
    endDate: string | null,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<Array<LabelValue>> {
		return await this.get<Array<LabelValue>>('/v1/charts/management-analytics/premium-by-month', {
			startDate,
      endDate,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse);
  }

  async premiumByBillmode(
    startDate: string | null,
    endDate: string | null,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<Array<PremiumByBillmodeData>> {
    type resultType = { billMode: string; policyPremium: number };
    return await this.get('/v1/charts/management-analytics/premium-by-billmode', {
      startDate,
      endDate,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse) as resultType[];
  }

  async premiumByWholesaleVsRetail(
    startDate: string | null,
    endDate: string | null,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<Array<PremiumByWholesaleVsRetailData>> {
    type resultType = { wholesale: number ; policyPremium: number };
    return await this.get('/v1/charts/management-analytics/premium-by-wholesale-vs-retail', {
      startDate,
      endDate,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse) as resultType[];
  }

  async premiumByCarrier(
    startDate: string | null,
    endDate: string | null,
    limit: number,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    type resultType = { carrier: string; agencyName: string; sourceAgencyKey: string, value: number };
    const result = (await this.get('/v1/charts/management-analytics/premium-by-carrier', {
      startDate,
      endDate,
      limit,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse)) as resultType[];

    const carriers = [...new Set(result.map((r) => r.carrier))];

    const agencies = new Map<string, {name: string, key: string}>();
    result.forEach((r) => agencies.set(r.agencyName, {name: r.agencyName, key: r.sourceAgencyKey}));

    const categories = [{ category: carriers.map((carrier) => ({ label: carrier })) }];

    const valueOf = (carrier: string, agencyName: string): { value?: number; color?: string } => {
      const data = result.find((r) => r.carrier === carrier && r.agencyName === agencyName);
      if (data) {
        return { value: data.value > 0 ? data.value : undefined };
      }
      return { value: undefined };
    };

    const sortedAgencies = Array.from(agencies.values()).sort((a, b) => agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1);

    const dataset = sortedAgencies.map((agency) => ({
      seriesname: agency.name,
      data: carriers.flatMap((carrier) => valueOf(carrier, agency.name)),
      color: colorsBySourceAgencyKey.get(agency.key),
    }));

    return { categories, dataset };
  }

  async premiumByDepartment(
    startDate: string | null,
    endDate: string | null,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[]
  ): Promise<Array<LabelValue>> {
    return await this.get<Array<LabelValue>>('/v1/charts/management-analytics/premium-by-department', {
      startDate,
      endDate,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse);
  }

  async premiumByIndustry(
    startDate: string | null,
    endDate: string | null,
    limit: number,
    departments: string[],
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    coverageTypes: string[],
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<MultiSeries> {
    type resultType = { industry: string; agencyName: string; sourceAgencyKey: string, value: number };
    const result = (await this.get('/v1/charts/management-analytics/premium-by-industry', {
      startDate,
      endDate,
      limit,
      departments,
      sicIndustries,
      industries,
      includeChildren,
      coverageTypes,
      agencyTypes,
      profitCenters,
    }).then(this.handleResponse)) as resultType[];
    const industryDescriptions = [...new Set(result.map((r) => r.industry))].slice(0, limit);

    const agencies = new Map<string, {name: string, key: string}>();
    result.forEach((r) => agencies.set(r.agencyName, {name: r.agencyName, key: r.sourceAgencyKey}));

    const categories = [{ category: industryDescriptions.map((industryDescription) => ({ label: industryDescription })) }];
    const valueOf = (industry: string, agencyName: string): { value?: number; color?: string } => {
      const data = result.find((r) => r.industry === industry && r.agencyName === agencyName);
      if (data) {
        return { value: data.value > 0 ? data.value : undefined };
      }
      return { value: undefined };
    };

    const sortedAgencies = Array.from(agencies.values()).sort((a, b) => agencyLegendOrder.indexOf(a.key) > agencyLegendOrder.indexOf(b.key) ? 1 : -1);

    const dataset = sortedAgencies.map((agency) => ({
      seriesname: agency.name,
      data: industryDescriptions.flatMap((industry) => valueOf(industry, agency.name)),
      color: colorsBySourceAgencyKey.get(agency.key),
    }));
    return { categories, dataset };
  }

  async premiumByProduct(
    sicIndustries: string[],
    industries: number[],
    includeChildren: boolean,
    agencyTypes: string[],
    profitCenters: number[],
  ): Promise<LabelValueYearT12[]> {
    return (await this.get('/v1/charts/management-analytics/premium-by-product', {
      sicIndustries,
      industries,
      includeChildren,
      agencyTypes,
      profitCenters,
    }).then(
      this.handleResponse,
    )) as LabelValueYearT12[];
  }

  async carrierServiceAgreementCompensation(agencyTypes: string[]): Promise<MultiSeries> {
    return (await this.get('/v1/charts/management-analytics/service-agreement-compensation', { agencyTypes }).then(
      this.handleResponse,
    )) as MultiSeries;
  }
}

export const managementAnalyticsService = new ManagementAnalyticsService();
